<template>
    <div
        class="c-one-time-code"
        :class="{ 'is-focused': focused }"
        role="group"
    >
        <span
            v-for="(_, index) in boxes"
            :key="index"
            class="c-one-time-code__box"
            :class="{ 'is-active': index === active, 'is-filled': index < value.length }"
            aria-hidden="true"
        >{{ value.charAt(index) }}</span>
        <input
            :id="inputId"
            ref="input"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            pattern="[0-9]*"
            name="one_time_code"
            class="c-one-time-code__input"
            :value="value"
            :aria-label="ariaLabel"
            :aria-describedby="describedBy || null"
            @input="onInput"
            @paste.prevent="onPaste"
            @focus="onFocus"
            @blur="focused = false"
            @click="moveCaretToEnd"
        >
    </div>
</template>

<script>
import { CODE_LENGTH, activeBoxIndex, nextCode } from '../one-time-code';

/**
 * PLAT-85 — six presentational boxes drawn over ONE real input, shared by login and guest apply.
 * One field means a keyboard suggestion / autofill is a single insertion (AC-5) and there is nothing to
 * spread across boxes. No maxlength on purpose: a pasted "123 456" is 7 characters and must be
 * sanitised, not truncated by the browser.
 */
export default {
    name: 'OneTimeCodeInput',

    props: {
        value: { type: String, default: '' },
        length: { type: Number, default: CODE_LENGTH },
        inputId: { type: String, required: true },
        ariaLabel: { type: String, default: 'Verification code' },
        describedBy: { type: String, default: '' },
    },

    data() {
        return { focused: false };
    },

    computed: {
        boxes() {
            return Array.from({ length: this.length });
        },

        active() {
            return activeBoxIndex(this.value, this.focused, this.length);
        },
    },

    methods: {
        onInput(event) {
            const el = event.target;
            const next = nextCode(this.value, el.value, event.data, this.length);
            if (el.value !== next) el.value = next;
            if (next !== this.value) this.$emit('input', next);
            this.moveCaretToEnd();
        },

        onPaste(event) {
            const text = (event.clipboardData || window.clipboardData).getData('text');
            const next = nextCode(this.value, this.value + text, text, this.length);
            this.$refs.input.value = next;
            if (next !== this.value) this.$emit('input', next);
        },

        onFocus() {
            this.focused = true;
            this.moveCaretToEnd();
        },

        moveCaretToEnd() {
            const el = this.$refs.input;
            if (el && el.setSelectionRange) {
                const end = el.value.length;
                el.setSelectionRange(end, end);
            }
        },

        focus(options) {
            if (this.$refs.input) this.$refs.input.focus(options);
        },
    },
};
</script>

<style>
.c-one-time-code {
    position: relative;
    display: flex;
    justify-content: center;
    gap: 7px;
    margin: 0 auto 0.75rem;
}

.c-one-time-code__box {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 46px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background: #fff;
    font-size: 18px;
    font-weight: 400;
    color: #333;
}

.c-one-time-code__box.is-active {
    border: 2px solid #6e45a0;
    box-shadow: 0 0 0 3px rgba(110, 69, 160, 0.18);
}

.c-one-time-code .c-one-time-code__input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    border: 0;
    box-shadow: none;
    background: transparent;
    opacity: 0;
    /* 16px or more: iOS Safari zooms on focus below it */
    font-size: 16px;
    caret-color: transparent;
}

@media screen and (min-width: 40em) {
    .c-one-time-code {
        gap: 10px;
    }

    .c-one-time-code__box {
        width: 48px;
        height: 58px;
        font-size: 22px;
    }
}
</style>
